import HttpWrapper from '@/utils/http/httpWrapper'

import ShipbobAppSettings from '../../appSettings/appSetting'
import toastr from 'toastr'
import { HTTP_METHODS } from '@/utils/http/httpBuilder'
import { ProductService } from '@/services/product/index'

export const RECEIVING_API = ShipbobAppSettings.receivingApi
export const MERCHANT_WRO_CONTROLLER = '/api/warehouseReceivingOrders'
export const MERCHANT_WRO_CONTROLLER_V2 = '/api/v2/WarehouseReceivingOrders'
export const WMS_API = ShipbobAppSettings.wmsApi
export const RECEIVING_PUBLIC_API = ShipbobAppSettings.receivingPublicApi
export const INBOUND_MANAGEMENT_API = ShipbobAppSettings.inboundManagementApi
export const PRODUCT_MANAGEMENT_API = ShipbobAppSettings.productsManagementApi

class WroServiceInternal {
  urls () {
    return {
      baseWMS: WMS_API + '/api/warehouse-receiving-orders',
      cancelWro: RECEIVING_PUBLIC_API + '/{id}/cancel',
      get: RECEIVING_API + MERCHANT_WRO_CONTROLLER + '/filter',
      getV2: RECEIVING_API + MERCHANT_WRO_CONTROLLER_V2 + '/filter',
      getProductsByNameOrIdForCreateV2: PRODUCT_MANAGEMENT_API + '/api/product',
      getSpokesForHubId: INBOUND_MANAGEMENT_API + '/api/v1/hub/{id}/spokes',
      getTransferPlan: INBOUND_MANAGEMENT_API + '/api/v1/transferplan',
      getWroDetailsV2: RECEIVING_API + MERCHANT_WRO_CONTROLLER + '/{id}/merchantSummary',
      getWroSummary: RECEIVING_API + MERCHANT_WRO_CONTROLLER,
      getWroInventoryQuantityDetails: RECEIVING_API + MERCHANT_WRO_CONTROLLER + '/{id}/inventoryQuantityDetails'
    }
  }

  async get ({ Filters, PageSize, PageNumber, SortColumn, Descending }) {
    const request = {
      Url: this.urls().get,
      Data: { Filters, PageSize, PageNumber, SortColumn, Descending }
    }
    return await HttpWrapper.Post(request)
  }

  async getV2 ({ Filters, PageSize, PageNumber, SortColumn, Descending }) {
    const request = {
      Url: this.urls().getV2,
      Data: { Filters, PageSize, PageNumber, SortColumn, Descending }
    }
    return await HttpWrapper.Post(request)
  }

  async getWroDetailsV2 (id) {
    const request = {
      Url: `${this.urls().getWroDetailsV2.replace('{id}', id)}`
    }
    return await HttpWrapper.Get(request)
  }

  async getTransferPlan (id) {
    const request = {
      Url: `${this.urls().getTransferPlan}/referenceid/${id}`
    }
    return await HttpWrapper.Get(request, undefined, undefined, true)
  }

  async getTransferPlans (data) {
    const stringIds = data.referenceIds.join()
    const request = {
      Url: `${this.urls().getTransferPlan}?ReferenceIds=${stringIds}&ReferenceType=${data.referenceType}`
    }
    return await HttpWrapper.Get(request, undefined, undefined, true)
  }

  async cancelWro (id) {
    const request = {
      Url: `${this.urls().cancelWro.replace('{id}', id)}`
    }
    const res = await HttpWrapper.Post(request, null, (error) => {
      const errorList = error.response.data
      for (const key in errorList) {
        errorList[key].forEach(val => {
          toastr.error(val)
        })
      }
    }, true)
    if (res) {
      toastr.success('WRO has been cancelled.')
      return res
    }
    return false
  }

  async getSpokesForHubId (id) {
    const request = {
      Url: `${this.urls().getSpokesForHubId.replace('{id}', id)}`
    }
    return await HttpWrapper.Get(request, undefined, undefined, true)
  }

  async getWroMetadata (id) {
    const request = {
      Url: `${this.urls().getWroSummary}/${id}`
    }
    const [wroSummary, wroInventoryQuantityDetails] = await Promise.all([
      HttpWrapper.Get(request),
      HttpWrapper.Get({ Url: this.urls().getWroInventoryQuantityDetails.replace('{id}', id) })
    ])
    let productsMetadata = []

    // Take 5 products (no real order)
    const topFiveWroInventoryQuantityDetails = wroInventoryQuantityDetails.inventoryQuantityDetails?.slice(0, 5)
    if (topFiveWroInventoryQuantityDetails?.length) {
      productsMetadata = await ProductService.getProductsMetadataByIds(topFiveWroInventoryQuantityDetails.map(prod => prod.id))
    }
    return { wroSummary, wroInventoryQuantityDetails, topFiveWroInventoryQuantityDetails, productsMetadata }
  }

  async getProductsByNameOrIdForCreateV2 (searchTerm) {
    const request = {
      Url: `${this.urls().getProductsByNameOrIdForCreateV2}?search=${searchTerm}`
    }
    return await HttpWrapper.Get(request)
  }

  async updateCompleteWro (dataRequest) {
    const request = {
      Url: `${this.urls().baseWMS}/${dataRequest.wroId}/updateCompleteWro`,
      Data: dataRequest
    }
    let serviceDidNotFail = true
    await HttpWrapper.Put(request, null, (errorResponse) => {
      let message = 'Something went wrong, please try again later.'

      if (errorResponse.response.data.message) {
        const mainErrorMessage = errorResponse.response.data.message
        const detailedErrorMessage = errorResponse.response.data.details.join()
        message = `${mainErrorMessage}: ${detailedErrorMessage}`
      } else {
        message = errorResponse.response.data
      }

      if (message) {
        toastr.error(message)
      }

      serviceDidNotFail = false
    }, true)
    return serviceDidNotFail
  }

  async updateWro ({ wroId, fulfillmentCenterId, editEstimatedArrivalDate, poNumber }) {
    const request = {
      Url: `${this.urls().baseWMS}/${wroId}/updateWro`,
      Data: {
        FulfillmentCenterId: fulfillmentCenterId,
        ExpectedArrivalDate: editEstimatedArrivalDate,
        PurchaseOrderNumber: poNumber
      }
    }
    const res = await HttpWrapper.Exec({
      type: HTTP_METHODS.PUT,
      request,
      disableErrorMessages: true,
      successStrategy: () => ({
        success: true,
        message: 'Successfully updated Wro.'
      }),
      failedStrategy: ({ response: { data, status } }) => {
        return {
          success: false,
          message: status === 500 ? 'Something went wrong, please try again later.' : data
        }
      }
    })
    if (!res) return { success: true, message: 'WRO Updated.' }
    return res
  }

  async updatePackagingDetails (wroId, { trackingNumbers, vendorCarrierName, otherCarrierName }) {
    const request = {
      Url: `${this.urls().baseWMS}/${wroId}/packagingDetails`,
      Data: {
        UpdateTrackingNumbers: trackingNumbers,
        VendorCarrierName: vendorCarrierName,
        OtherCarrierName: otherCarrierName
      }
    }
    if (await HttpWrapper.Put(request, null, null, true)) {
      toastr.success('Successfully updated packaging details')
    } else {
      toastr.error('Failed to update packaging details. Please try again or contact support')
    }
  }
}

export const WroService = new WroServiceInternal()
